import { PASSWORD_MIN_LENGTH } from "@esi/shared";
import { Check, Circle } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/**
 * The account password policy (user pool: at least 12 characters with upper and lower case, a
 * digit and a symbol). Only the length is enforced here; the server has the final word.
 */
const RULES = [
  { key: "length", test: (v: string) => v.length >= PASSWORD_MIN_LENGTH },
  { key: "upper", test: (v: string) => /[A-Z]/.test(v) },
  { key: "lower", test: (v: string) => /[a-z]/.test(v) },
  { key: "digit", test: (v: string) => /[0-9]/.test(v) },
  { key: "symbol", test: (v: string) => /[^A-Za-z0-9]/.test(v) },
] as const;

/** A new password with the policy as hints, ticked as they are met. */
export function NewPasswordField(props: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  invalid?: boolean;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`${id}-password`}>{props.label ?? t("password.new")}</Label>
      <Input
        id={`${id}-password`}
        type="password"
        name="new-password"
        autoComplete="new-password"
        required
        minLength={PASSWORD_MIN_LENGTH}
        autoFocus={props.autoFocus}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        aria-describedby={`${id}-hints`}
        aria-invalid={props.invalid ? true : undefined}
      />
      <ul id={`${id}-hints`} className="flex flex-col gap-0.5 pt-1 text-xs text-fg-muted">
        {RULES.map((rule) => {
          const met = rule.test(props.value);
          const Icon = met ? Check : Circle;
          return (
            <li
              key={rule.key}
              className={cn("flex items-center gap-1.5", met && "text-success-fg")}
            >
              <Icon aria-hidden="true" className="size-3" strokeWidth={2} />
              {t(`password.rules.${rule.key}`, { min: PASSWORD_MIN_LENGTH })}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export const passwordLongEnough = (value: string) => value.length >= PASSWORD_MIN_LENGTH;
